import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';
import {
  BrandPalette,
  brandDark,
  brandLight,
  fontMono,
  fontSans,
} from './colors';

/**
 * Backstage's own components are styled with v4 `makeStyles({ name })` rather
 * than MUI's component slots, so their names have to be declared before the
 * theme can override them.
 */
declare module '@backstage/theme' {
  interface OverrideComponentNameToClassKeys {
    BackstageItemCardGrid: 'root';
    BackstageItemCardHeader: 'root';
  }
}

/**
 * Everything that is a choice about the variant rather than a color, so the
 * two themes below differ only in the palette they are handed and this.
 */
type Variant = {
  mode: 'dark' | 'light';
  /** Base palette from @backstage/theme, for the keys not overridden here. */
  base: typeof palettes.dark | typeof palettes.light;
  /**
   * The two stops of the page header's gradient wave. Dark lifts off the page
   * with `surface` -> `elevated`; light has to go the other way and step down
   * from a near-white page, or the header vanishes into it.
   */
  header: [keyof BrandPalette, keyof BrandPalette];
};

function createBrandTheme(brand: BrandPalette, variant: Variant) {
  const headerGradient = genPageTheme({
    colors: [brand[variant.header[0]], brand[variant.header[1]]],
    shape: shapes.wave,
    options: { fontColor: brand.text },
  });

  return createUnifiedTheme({
    fontFamily: fontSans,
    palette: {
      ...variant.base,
      type: variant.mode,
      mode: variant.mode,

      background: {
        default: brand.bg,
        paper: brand.surface,
      },

      primary: { main: brand.accent },
      secondary: { main: brand.merged },
      error: { main: brand.danger },
      warning: { main: brand.warning },
      success: { main: brand.success },
      info: { main: brand.accent },

      text: {
        primary: brand.text,
        secondary: brand.muted,
      },
      textSubtle: brand.muted,
      textVerySubtle: brand.dim,
      textContrast: brand.contrast,

      divider: brand.border,
      border: brand.border,

      link: brand.accent,
      linkHover: brand.text,

      status: {
        ok: brand.success,
        warning: brand.warning,
        error: brand.danger,
        pending: brand.dim,
        running: brand.accent,
        aborted: brand.muted,
      },

      errorBackground: brand.dangerDim,
      warningBackground: brand.warningDim,
      infoBackground: brand.accentDim,
      errorText: brand.danger,
      warningText: brand.warning,
      infoText: brand.accent,

      banner: {
        info: brand.accent,
        error: brand.danger,
        warning: brand.warning,
        text: brand.text,
        link: brand.accent,
        closeButtonColor: brand.muted,
      },

      navigation: {
        background: brand.surface,
        indicator: brand.accent,
        color: brand.muted,
        selectedColor: brand.text,
        navItem: { hoverBackground: brand.hover },
        submenu: { background: brand.elevated },
      },

      tabbar: { indicator: brand.accent },

      pinSidebarButton: {
        icon: brand.dim,
        background: brand.elevated,
      },

      code: { background: brand.elevated },
    },
    defaultPageTheme: 'home',
    pageTheme: {
      home: headerGradient,
      documentation: headerGradient,
      tool: headerGradient,
      service: headerGradient,
      website: headerGradient,
      library: headerGradient,
      other: headerGradient,
      app: headerGradient,
      apis: headerGradient,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: brand.bg,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          'code, kbd, pre': {
            fontFamily: fontMono,
          },
          '::-webkit-scrollbar': { width: 5, height: 5 },
          '::-webkit-scrollbar-track': { background: brand.bg },
          '::-webkit-scrollbar-thumb': {
            background: brand.borderBright,
            borderRadius: 2,
          },
          '::-webkit-scrollbar-thumb:hover': { background: brand.muted },
        },
      },

      /*
       * The header of a template card on /create. Out of the box it paints the
       * page theme's gradient wave across the top of every card, which is the
       * loudest thing on a page whose whole job is to list a handful of
       * templates. Flattened to the elevated surface with a hairline under it,
       * so the card reads as one object and the title is what stands out.
       */
      BackstageItemCardHeader: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: brand.elevated,
            borderBottom: `1px solid ${brand.border}`,
            color: brand.text,
            padding: 16,

            // The entity type, rendered above the title. Demoted to a label so
            // it reads as a category rather than competing with the name.
            '& h3': {
              color: brand.muted,
              fontSize: 11,
              letterSpacing: '0.07em',
              lineHeight: 1.6,
              textTransform: 'uppercase',
            },

            '& h4': {
              fontWeight: 600,
            },
          },
        },
      },

      /*
       * Everything inside the template grid. Scoped here rather than at
       * `MuiCard` so the entity overview cards, which are Backstage UI and
       * already carry these, are left alone. The `data-testid` hooks are the
       * card's own, and are the only stable handles it exposes - its class
       * names are generated per build.
       */
      BackstageItemCardGrid: {
        styleOverrides: {
          root: {
            gap: 'var(--bui-space-4)',

            '& .MuiCard-root': {
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: brand.surface,
              border: `1px solid ${brand.border}`,
              borderRadius: 'var(--bui-radius-3)',
              boxShadow: 'none',
              transition: 'border-color 120ms ease',
            },

            '& .MuiCard-root:hover, & .MuiCard-root:focus-within': {
              borderColor: brand.borderBright,
            },

            /*
             * The description clamps at ten lines by default, which lets one
             * wordy template set the height of every card in its row - the grid
             * gives them all the height of the tallest. Four is enough to tell
             * templates apart; the rest is on the template's own page.
             */
            '& [data-testid="template-card-content-container"]': {
              WebkitLineClamp: 4,
              color: brand.muted,
            },

            '& [data-testid="template-card-tags"] .MuiChip-root': {
              backgroundColor: brand.elevated,
              border: `1px solid ${brand.border}`,
              color: brand.muted,
            },

            // Dividers inside the card, against a surface that is already
            // separated from the page by its own border.
            '& .MuiDivider-root': {
              backgroundColor: brand.border,
            },
          },
        },
      },
    },
  });
}

export const devTheme = createBrandTheme(brandDark, {
  mode: 'dark',
  base: palettes.dark,
  header: ['surface', 'elevated'],
});

export const devLightTheme = createBrandTheme(brandLight, {
  mode: 'light',
  base: palettes.light,
  header: ['elevated', 'hover'],
});
